import { Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { Stack, TextField } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import Fade from 'react-reveal/Fade'

import {
  AgencySettings_EnabledFeaturesEnum,
  PolicyTimeslot_DaysEnum,
  PolicyRule_VehicleTypesEnum,
  PolicyTimeslot,
} from 'src/api/gen/api'
import { IPolicyRuleParking } from 'src/api/types/policy'
import { DistanceUnit } from 'src/api/types/units'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { isEndTimeBeforeStartTime } from 'src/commons/policies/utils'
import { useAgencySettings, useCheckEnabledFeature } from 'src/layout/AgencySettings'
import SETTINGS from 'src/settings'

import { FieldTitle } from '../../commons'
import { GeographyField } from '../fields'
import { useSmartPatrolContext } from '../hooks/SmartPatrolWrapper'

import TimeSlotForm from './TimeSlot'

function useStyles() {
  return makeStyles(() => ({
    inlineFlexFieldWrapper: { display: 'flex', gap: '1rem', alignItems: 'center' },
    inlineFlexField: { minWidth: '65%' },
    inlineFlexTitleField: { minWidth: '30%' },
  }))()
}

function ParkingRule(props: IBaseTableProps<IPolicyRuleParking>) {
  const { t } = useTranslation()
  const classes = useStyles()
  const buffered_areas_enabled = useCheckEnabledFeature()(AgencySettings_EnabledFeaturesEnum.buffered_areas)

  const agencySettings = useAgencySettings()
  const { smartPatrolActive, smartPatrolThreshold } = useSmartPatrolContext()

  const handleChangeRule =
    (ruleId: string, key: keyof IPolicyRuleParking) =>
    (val: string | string[] | number | PolicyRule_VehicleTypesEnum[] | null): void => {
      const newRule: IPolicyRuleParking[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }
        return { ...rule, [key]: val }
      })
      // we are using simple selector for prohibited/authorized zones but we actually still store it in an array for now
      // so we need to update the rule accordingly
      if (!Array.isArray(newRule[0].prohibited_parking_zones)) {
        newRule[0].prohibited_parking_zones = [newRule[0].prohibited_parking_zones ?? '']
      }
      if (!Array.isArray(newRule[0].buffered_prohibited_parking_zones)) {
        newRule[0].buffered_prohibited_parking_zones = [newRule[0].buffered_prohibited_parking_zones ?? '']
      }
      if (!Array.isArray(newRule[0].authorized_parking_zones)) {
        newRule[0].authorized_parking_zones = [newRule[0].authorized_parking_zones ?? '']
      }
      if (!Array.isArray(newRule[0].buffered_authorized_parking_zones)) {
        newRule[0].buffered_authorized_parking_zones = [newRule[0].buffered_authorized_parking_zones ?? '']
      }
      props.handleChange(newRule)
    }

  function handleChangeTimeslot(timeslot: PolicyTimeslot, index: number, key: keyof PolicyTimeslot) {
    return (val: string | PolicyTimeslot_DaysEnum[] | null): void => {
      const timeslotsToUpdate: PolicyTimeslot[] = props.rules[0].timeslots
      timeslotsToUpdate[index] = { ...timeslot, [key]: val }
      const newRules: IPolicyRuleParking[] = [{ ...props.rules[0], ['timeslots']: timeslotsToUpdate }]
      props.handleChange(newRules)
    }
  }

  const {
    rule_id,
    prohibited_parking_zones,
    buffered_prohibited_parking_zones,
    authorized_parking_zones,
    buffered_authorized_parking_zones,
  } = props.rules[0]

  return (
    <>
      <Grid container direction="column" spacing={3} style={{ marginTop: -35 }}>
        <Grid container direction="row" spacing={10} style={{ paddingLeft: '9px' }}>
          <Grid container item xs={6} spacing={5}>
            <div className={classes.inlineFlexFieldWrapper}>
              <div className={classes.inlineFlexTitleField}>
                <FieldTitle
                  title={t('rule.prohibited_parking_zones')}
                  tooltip={t('rule.prohibited_parking_zones.tooltip')}
                />
              </div>
              <div className={classes.inlineFlexField}>
                <GeographyField
                  id="prohibited_parking_all_four_of_geo_selection"
                  value={prohibited_parking_zones[0]}
                  allGeographies={props.allGeographies}
                  handleChange={handleChangeRule(rule_id, 'prohibited_parking_zones')}
                  disabled={props.disabled}
                  helperText={props.validation[0].getFieldErrorMsg('geographies')}
                />
              </div>
            </div>
          </Grid>
          {buffered_areas_enabled ? (
            <Grid item xs={6}>
              <div className={classes.inlineFlexFieldWrapper}>
                <div className={classes.inlineFlexTitleField}>
                  <FieldTitle
                    title={t('rule.buffered_prohibited_parking_zones')}
                    tooltip={t('rule.buffered_prohibited_parking_zones.tooltip')}
                  />
                </div>
                <div className={classes.inlineFlexField}>
                  <GeographyField
                    id="prohibited_parking_buffered_geography"
                    value={buffered_prohibited_parking_zones[0]}
                    allGeographies={props.allGeographies}
                    handleChange={handleChangeRule(rule_id, 'buffered_prohibited_parking_zones')}
                    disabled={props.disabled || prohibited_parking_zones!.length === 0}
                  />
                </div>
              </div>
            </Grid>
          ) : null}

          <Grid container item xs={6} spacing={5}>
            <div className={classes.inlineFlexFieldWrapper}>
              <div className={classes.inlineFlexTitleField}>
                <FieldTitle
                  title={t('rule.authorized_parking_zones')}
                  tooltip={t('rule.authorized_parking_zones.tooltip')}
                />
              </div>
              <div className={classes.inlineFlexField}>
                <GeographyField
                  value={authorized_parking_zones[0]}
                  allGeographies={props.allGeographies}
                  handleChange={handleChangeRule(rule_id, 'authorized_parking_zones')}
                  disabled={props.disabled}
                  helperText={props.validation[0].getFieldErrorMsg('geographies')}
                />
              </div>
            </div>
          </Grid>
          {buffered_areas_enabled ? (
            <Grid item xs={6}>
              <div className={classes.inlineFlexFieldWrapper}>
                <div className={classes.inlineFlexTitleField}>
                  <FieldTitle
                    title={t('rule.buffered_authorized_parking_zones')}
                    tooltip={t('rule.buffered_authorized_parking_zones.tooltip')}
                  />
                </div>
                <div className={classes.inlineFlexField}>
                  <GeographyField
                    value={buffered_authorized_parking_zones[0]}
                    allGeographies={props.allGeographies}
                    handleChange={handleChangeRule(rule_id, 'buffered_authorized_parking_zones')}
                    disabled={props.disabled || authorized_parking_zones!.length === 0}
                  />
                  {smartPatrolActive && !props.isPublished && (
                    <Alert severity="info">
                      {t('rule.buffered_authorized_parking_zones.alert', {
                        value: smartPatrolThreshold || agencySettings.smart_patrol_parking_threshold,
                        unit: DistanceUnit.Meter,
                      })}
                    </Alert>
                  )}
                </div>
              </div>
            </Grid>
          ) : null}
        </Grid>
        <Grid container direction="row" style={{ paddingLeft: '9px', marginTop: 30 }}>
          <Grid container item xs={6} spacing={5}>
            <Fade when={smartPatrolActive}>
              <Stack alignItems="center" direction="row" gap={3} sx={{ fontWeight: 'bold' }}>
                <Typography>{t('rule.smartpatrol_threshold')}</Typography>
                <TextField
                  disabled
                  inputProps={{ sx: { height: 10, padding: '10px 14px' } }}
                  value={smartPatrolThreshold}
                />
                <Typography>{DistanceUnit.Meter}</Typography>
              </Stack>
            </Fade>
          </Grid>
        </Grid>
      </Grid>

      {SETTINGS.app.enable_timeslots && (
        <Grid container spacing={3} style={{ marginTop: '20px' }}>
          {props.rules[0].timeslots.map((timeslot, index) => {
            return (
              <Grid item xs={3} key={index}>
                <Grid container direction="column" spacing={1} style={{ marginTop: '13px' }}>
                  <Grid item component="h3">
                    {t('rule.time_slot_count', { index: index + 1 })}
                  </Grid>
                  <Grid item>
                    <TimeSlotForm
                      timeslot={timeslot}
                      handleChangeDays={handleChangeTimeslot(timeslot, index, 'days')}
                      handleChangeStartTime={handleChangeTimeslot(timeslot, index, 'start_time')}
                      handleChangeEndTime={handleChangeTimeslot(timeslot, index, 'end_time')}
                      handleRemoveTimeSlot={() => props.handleRemoveTimeSlot(index)}
                      disabled={props.isPublished && props.disabled}
                      error={isEndTimeBeforeStartTime(timeslot)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
        </Grid>
      )}
    </>
  )
}

export default ParkingRule
